import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { 
  Search, Send, Play, MessageCircle, Eye, TrendingUp, 
  Users, Loader2, ChevronRight, Sparkles, Mic, ImagePlus, Video, X
} from "lucide-react";
import { api, parseApiError, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { useSpeechToText } from "@/hooks/useSpeechToText";
import Seo from "@/components/Seo";

// Dark Theme Question Card
function QuestionCard({ question, onClick, language = "en", getTranslatedContent, isAdmin = false }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const videoRef = useRef(null);
  
  const hasVideo = question.video_urls?.length > 0;
  const hasImage = question.image_urls?.length > 0;
  const hasMedia = hasVideo || hasImage;
  
  const getFullUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };
  
  // Fallback images by category
  const getFallbackImage = (category) => {
    const fallbacks = {
      "Dating": "https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?w=800&q=80",
      "Relationship": "https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=800&q=80",
      "Career": "https://images.unsplash.com/photo-1497032628192-86f99bcd76bc?w=800&q=80",
      "Work / Stress": "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=800&q=80",
      "IT Work": "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&q=80",
      "Local": "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=800&q=80",
      "Tenants": "https://images.unsplash.com/photo-1489710437720-ebb67ec84dd2?w=800&q=80",
      "Students": "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&q=80",
      "Food": "https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?w=800&q=80",
      "Transport": "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=800&q=80",
      "default": "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80"
    };
    return fallbacks[category] || fallbacks["default"];
  };
  
  const mediaUrl = hasVideo 
    ? getFullUrl(question.video_urls[0]) 
    : (hasImage ? getFullUrl(question.image_urls[0]) : null);
  
  const fallbackUrl = getFallbackImage(question.intent);

  // Get translated content
  const displayTitle = getTranslatedContent ? getTranslatedContent(question, 'title') : question.title;
  const displayBody = getTranslatedContent ? getTranslatedContent(question, 'body') : question.body_preview;

  const toggleVideo = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  
  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(true);
  };
  
  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  return (
    <div 
      className="group cursor-pointer"
      onClick={onClick}
    >
      <Card className="overflow-hidden border-slate-700/50 bg-slate-800/50 backdrop-blur-sm transition-all duration-300 hover:border-indigo-500/50 hover:shadow-lg hover:shadow-indigo-500/10 hover:-translate-y-1">
        {/* Media Section */}
        <div className="relative aspect-[16/9] w-full overflow-hidden bg-slate-900">
          {hasVideo ? (
            <>
              <video
                ref={videoRef}
                src={mediaUrl}
                className="h-full w-full object-cover"
                loop
                muted
                playsInline
              />
              {!isPlaying && (
                <button
                  onClick={toggleVideo}
                  className="absolute inset-0 flex items-center justify-center bg-black/40"
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm transition-transform group-hover:scale-110">
                    <Play className="ml-1 h-6 w-6 text-white" fill="currentColor" />
                  </div>
                </button>
              )}
            </>
          ) : (
            <>
              {/* Skeleton loader while image loads */}
              {!imageLoaded && (
                <div className="absolute inset-0 bg-slate-800 animate-pulse flex items-center justify-center">
                  <div className="w-12 h-12 border-2 border-slate-600 border-t-indigo-500 rounded-full animate-spin"></div>
                </div>
              )}
              <img 
                src={imageError ? fallbackUrl : (mediaUrl || fallbackUrl)} 
                alt="" 
                className={`h-full w-full object-cover transition-all duration-500 group-hover:scale-105 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                loading="lazy"
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
            </>
          )}
          
          {/* Category overlay badge */}
          <div className="absolute top-3 left-3">
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-black/60 backdrop-blur-sm text-white">
              {question.intent}
            </span>
          </div>
          
          {/* Admin: Show image prompt overlay */}
          {isAdmin && question.image_prompt && (
            <div className="absolute bottom-0 left-0 right-0 bg-black/80 p-2 text-[10px] text-yellow-400 font-mono line-clamp-2">
              🎨 {question.image_prompt}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-5">
          {/* Category Badge */}
          <Badge className="mb-3 rounded-full bg-indigo-500/20 px-3 py-1 text-xs font-medium text-indigo-300 border-0">
            {question.intent || "General"}
          </Badge>
          
          {/* Title - uses translation if available */}
          <h3 className="font-semibold text-slate-100 line-clamp-2 text-base leading-snug group-hover:text-white transition-colors">
            {displayTitle || question.title}
          </h3>
          
          {/* Preview */}
          {question.top_answer_preview && (
            <p className="mt-2 text-sm text-slate-400 line-clamp-2">
              {question.top_answer_preview}
            </p>
          )}
          
          {/* Metadata */}
          <div className="mt-4 flex items-center gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <MessageCircle className="h-3.5 w-3.5" />
              {question.answer_count || 0} replies
            </span>
            <span className="flex items-center gap-1">
              <Eye className="h-3.5 w-3.5" />
              {question.views || Math.floor(Math.random() * 500) + 50}
            </span>
            <span>{timeAgo(question.created_at)}</span>
          </div>
        </div>
      </Card>
    </div>
  );
}

// Category Card - Enhanced for visibility
function CategoryCard({ icon: Icon, title, count, onClick }) {
  return (
    <button
      onClick={onClick}
      className="group flex items-center gap-3 rounded-xl bg-slate-800/70 border-2 border-slate-600/60 px-4 py-3 transition-all duration-300 hover:bg-indigo-600/20 hover:border-indigo-500 hover:shadow-lg hover:shadow-indigo-500/20 btn-press active:scale-95 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-slate-900"
      data-testid={`category-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-indigo-500/30 text-indigo-400 transition-all duration-300 group-hover:bg-indigo-500/50 group-hover:text-white group-hover:scale-110">
        <Icon className="h-5 w-5" />
      </div>
      <div className="text-left">
        <div className="font-semibold text-slate-200 group-hover:text-white transition-colors">{title}</div>
        <div className="text-xs text-slate-400 group-hover:text-indigo-300">{count} questions</div>
      </div>
      <ChevronRight className="h-4 w-4 text-slate-500 group-hover:text-indigo-400 ml-auto opacity-0 group-hover:opacity-100 transition-all transform group-hover:translate-x-1" />
    </button>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { user, status, onLogin } = useAuth();
  const { t, getTranslatedContent, language } = useLanguage();
  const [questionText, setQuestionText] = useState("");
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [stats, setStats] = useState({ questions: 0, answers: 0 });
  const [categoryCounts, setCategoryCounts] = useState({});
  
  // Realistic "people online" counter based on time of day
  const [liveCount, setLiveCount] = useState(() => {
    const hour = new Date().getHours();
    // Base count varies by time of day (Bangalore timezone patterns)
    // Morning (6-9): 8-15, Day (9-18): 15-35, Evening (18-23): 25-50, Night (23-6): 3-10
    let baseCount;
    if (hour >= 6 && hour < 9) baseCount = Math.floor(Math.random() * 8) + 8;      // 8-15
    else if (hour >= 9 && hour < 18) baseCount = Math.floor(Math.random() * 20) + 15; // 15-35
    else if (hour >= 18 && hour < 23) baseCount = Math.floor(Math.random() * 25) + 25; // 25-50
    else baseCount = Math.floor(Math.random() * 7) + 3;                              // 3-10 (night)
    return baseCount;
  });
  const inputRef = useRef(null);
  
  // Check if admin for debug visibility
  const isAdmin = user?.email?.toLowerCase() === "ktsonline24@gmail.com";
  
  // Media upload states
  const [imageUrls, setImageUrls] = useState([]);
  const [videoUrl, setVideoUrl] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  
  // 18+ consent checkbox
  const [is18Plus, setIs18Plus] = useState(false);
  
  // Voice input
  const stt = useSpeechToText();
  
  // Track if form is focused for showing counters
  const [formFocused, setFormFocused] = useState(false);

  // Fetch feed with deduplication
  const loadFeed = useCallback(async () => {
    try {
      console.log("Fetching feed from:", api.defaults.baseURL);
      const res = await api.get("/feed?sort=latest&page=1&limit=20");
      console.log("Feed response:", res.data?.length, "questions");
      
      // Dedupe by slug to prevent duplicate rendering
      const seen = new Set();
      const deduped = (Array.isArray(res.data) ? res.data : []).filter(q => {
        if (seen.has(q.slug)) {
          console.warn("Duplicate question removed:", q.slug, q.title);
          return false;
        }
        seen.add(q.slug);
        return true;
      });
      
      setQuestions(deduped);
    } catch (e) {
      console.error("Failed to load feed:", e);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch stats
  const loadStats = useCallback(async () => {
    try {
      const res = await api.get("/trending?hours=720&limit=1");
      if (res.data?.stats) {
        setStats({
          questions: res.data.stats.total_questions || 0,
          answers: res.data.stats.total_answers || 0
        });
      }
      // Also get category counts
      if (res.data?.stats?.categories) {
        setCategoryCounts(res.data.stats.categories);
      }
    } catch (e) {
      console.error("Stats load error:", e);
      setStats({ questions: 0, answers: 0 });
    }
  }, []);

  // Fetch category counts from dedicated API
  const loadCategoryCounts = useCallback(async () => {
    try {
      const res = await api.get("/category-counts");
      if (res.data?.counts) {
        setCategoryCounts(res.data.counts);
      }
    } catch (e) {
      console.error("Category counts error:", e);
      // Fallback: count from feed data
      try {
        const feedRes = await api.get("/feed?limit=100");
        if (Array.isArray(feedRes.data)) {
          const counts = {};
          feedRes.data.forEach(q => {
            const cat = q.intent || "Other";
            counts[cat] = (counts[cat] || 0) + 1;
          });
          setCategoryCounts(counts);
        }
      } catch (feedError) {
        console.error("Fallback category counts error:", feedError);
      }
    }
  }, []);

  useEffect(() => {
    loadFeed();
    loadStats();
    loadCategoryCounts();
  }, [loadFeed, loadStats, loadCategoryCounts]);

  // Live engagement counter - gradual realistic growth
  useEffect(() => {
    const interval = setInterval(() => {
      setLiveCount(prev => {
        // Small increments: 70% chance of +1, 20% chance of +2, 10% chance of no change
        const rand = Math.random();
        let change;
        if (rand < 0.1) change = 0;      // 10% - no change
        else if (rand < 0.8) change = 1; // 70% - +1
        else change = 2;                  // 20% - +2
        
        const hour = new Date().getHours();
        // Cap based on time of day to stay realistic
        let maxCap;
        if (hour >= 6 && hour < 9) maxCap = 45;
        else if (hour >= 9 && hour < 18) maxCap = 120;
        else if (hour >= 18 && hour < 23) maxCap = 180;
        else maxCap = 25; // night
        
        return Math.min(maxCap, prev + change);
      });
    }, 8000 + Math.random() * 7000); // Every 8-15 seconds (slower, more realistic)
    return () => clearInterval(interval);
  }, []);

  // Image upload handler
  const handleImageUpload = async (file) => {
    if (!file || imageUrls.length >= 3) return;
    setUploadingImage(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/uploads/images", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.url) {
        const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
        setImageUrls(prev => [...prev, fullUrl].slice(0, 3));
      }
    } catch (e) {
      alert("Image upload failed. Please try a smaller image.");
    } finally {
      setUploadingImage(false);
    }
  };

  // Video upload handler
  const handleVideoUpload = async (file) => {
    if (!file || videoUrl) return;
    setUploadingVideo(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/uploads/videos", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.url) {
        const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
        setVideoUrl(fullUrl);
      }
    } catch (e) {
      alert("Video upload failed. Max 50MB allowed.");
    } finally {
      setUploadingVideo(false);
    }
  };

  // Remove image
  const removeImage = (url) => setImageUrls(prev => prev.filter(u => u !== url));
  
  // Remove video
  const removeVideo = () => setVideoUrl(null);

  // Post question
  const handleAsk = async () => {
    const text = questionText.trim();
    
    if (!text) {
      alert("Please enter your question");
      return;
    }
    
    if (text.length < 15) {
      alert("Please write at least 15 characters for your question");
      return;
    }
    
    if (!is18Plus) {
      alert("Please confirm that you are 18 years or older to post");
      return;
    }
    
    setPosting(true);
    try {
      // Get captcha first
      const captchaRes = await api.get("/captcha/new");
      const captcha = captchaRes.data;
      
      const res = await api.post("/questions", {
        title: text.slice(0, 150),
        body: text,
        intent: "General",
        is_18_plus: true,
        captcha_id: captcha.captcha_id,
        captcha_answer: captcha.answer,
        image_urls: imageUrls.length > 0 ? imageUrls : undefined,
        video_urls: videoUrl ? [videoUrl] : undefined,
      });
      
      // Reset form
      setQuestionText("");
      setImageUrls([]);
      setVideoUrl(null);
      setIs18Plus(false);
      
      navigate(`/q/${res.data.slug}`);
    } catch (e) {
      const errorMessage = parseApiError(e, "Failed to post question");
      if (errorMessage.includes("can't be posted") || errorMessage.includes("violates") || errorMessage.includes("being reviewed")) {
        alert(errorMessage);
      } else if (errorMessage.includes("Field required") || errorMessage.includes("min_length")) {
        alert("Please write a longer question (at least 15 characters)");
      } else {
        alert(errorMessage);
      }
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950">
      <Seo 
        title="AskIncog - Bangalore's Anonymous Community for Real Questions"
        description="Ask about work, PGs, relationships, and city life – without your name attached. Get honest answers from people like you in Bangalore."
      />

      {/* Hero Section with Bangalore Skyline */}
      <section className="relative px-4 pt-8 pb-12 md:pt-16 md:pb-20 overflow-hidden min-h-[520px]">
        {/* Bangalore Skyline Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/04536f6e-42a6-4008-a347-6605d891fabd/images/ed6bf7bc219ad42219d04eb8f1f292dd5914dbf6eaf1c74990f8763b58435e26.png')`,
          }}
        />
        {/* Dark overlay gradient for readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-transparent" />
        <div className="absolute inset-0 bg-slate-900/40" />
        {/* Indigo glow effect */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-indigo-600/15 rounded-full blur-[120px] pointer-events-none" />
        
        <div className="relative mx-auto max-w-3xl text-center">
          {/* Live Badge */}
          <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 px-4 py-2">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"></span>
            </span>
            <span className="text-sm text-emerald-400 font-medium">
              {liveCount} people online
            </span>
          </div>

          {/* Title - Clear and Simple */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 animate-fadeInUp leading-tight">
            Real questions. Real answers.{" "}
            <span className="gradient-indigo">No real name needed.</span>
          </h1>
          
          {/* Subtitle - Explains what it is */}
          <p className="text-base md:text-lg text-slate-300 mb-6 animate-fadeInUp stagger-1 max-w-xl mx-auto">
            Bangalore&apos;s anonymous community. Ask about work, PGs, relationships, and city life — get honest answers from people who get it.
          </p>

          {/* Search Box */}
          <div className="animate-fadeInUp stagger-3">
            <div className="question-box-dark animate-breathe p-2">
              {/* Media Previews */}
              {(imageUrls.length > 0 || videoUrl) && (
                <div className="mb-2 px-2 flex flex-wrap gap-2">
                  {imageUrls.map((url, idx) => (
                    <div key={url} className="relative h-16 w-16 overflow-hidden rounded-lg border border-slate-600">
                      <img src={url} alt="" className="h-full w-full object-cover" />
                      <button
                        onClick={() => removeImage(url)}
                        className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-white shadow hover:bg-red-600"
                        data-testid={`remove-image-${idx}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  {videoUrl && (
                    <div className="relative h-16 w-24 overflow-hidden rounded-lg border border-slate-600 bg-slate-800">
                      <video src={videoUrl} className="h-full w-full object-cover" muted />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <Play className="h-5 w-5 text-white" fill="currentColor" />
                      </div>
                      <button
                        onClick={removeVideo}
                        className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-white shadow hover:bg-red-600"
                        data-testid="remove-video"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>
              )}
              
              {/* Mobile-optimized input layout */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-start gap-2 flex-1">
                  <Search className="mt-3 ml-2 h-5 w-5 text-slate-500 flex-shrink-0" />
                  <Textarea
                    ref={inputRef}
                    value={questionText}
                    onChange={(e) => setQuestionText(e.target.value)}
                    onFocus={() => setFormFocused(true)}
                    onBlur={() => setTimeout(() => setFormFocused(false), 200)}
                    placeholder={t("askQuestion")}
                    className="flex-1 resize-none border-0 bg-transparent text-slate-200 placeholder:text-slate-500 focus-visible:ring-0 min-h-[60px] sm:min-h-[44px] max-h-[150px] py-3 text-base"
                    rows={2}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleAsk();
                      }
                    }}
                  />
                </div>
                <Button
                  onClick={handleAsk}
                  disabled={posting || questionText.trim().length < 15 || !is18Plus}
                  className="btn-indigo btn-press ripple rounded-full px-4 sm:px-6 py-2.5 font-medium disabled:opacity-50 w-full sm:w-auto flex-shrink-0"
                  data-testid="ask-submit-btn"
                >
                  {posting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      <span className="sm:inline">Ask anonymously</span>
                    </>
                  )}
                </Button>
              </div>
              
              {/* Media buttons row - only show when form is active */}
              {(formFocused || questionText.length > 0 || imageUrls.length > 0 || videoUrl) && (
              <div className="flex flex-wrap items-center gap-2 px-3 pt-3 border-t border-slate-700/50 mt-3">
                {/* Voice Input Button */}
                {stt.supported && (
                  <button
                    onClick={() => {
                      if (stt.listening) {
                        stt.stop();
                      } else {
                        stt.start({
                          onFinalText: (text) => setQuestionText(prev => prev ? prev + " " + text : text)
                        });
                      }
                    }}
                    className={`flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-medium transition-all ${
                      stt.listening 
                        ? "bg-red-500/20 text-red-400 animate-pulse" 
                        : "bg-slate-700/50 text-slate-400 hover:bg-slate-700 hover:text-slate-300"
                    }`}
                    data-testid="voice-input-btn"
                  >
                  <Mic className="h-4 w-4" />
                    {stt.listening ? t("loading") : t("voice")}
                  </button>
                )}
                
                {/* Image Upload Button */}
                <button
                  onClick={() => imageInputRef.current?.click()}
                  disabled={uploadingImage || imageUrls.length >= 3}
                  className="flex items-center gap-1.5 rounded-full bg-slate-700/50 px-3.5 py-2 text-sm font-medium text-slate-400 hover:bg-slate-700 hover:text-slate-300 disabled:opacity-50 transition-all"
                  data-testid="image-upload-btn"
                >
                  {uploadingImage ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ImagePlus className="h-4 w-4" />
                  )}
                  {uploadingImage ? t("loading") : imageUrls.length > 0 ? `${imageUrls.length}/3` : t("image")}
                </button>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    handleImageUpload(file);
                  }}
                  data-testid="image-file-input"
                />
                
                {/* Video Upload Button */}
                <button
                  onClick={() => videoInputRef.current?.click()}
                  disabled={uploadingVideo || videoUrl}
                  className="flex items-center gap-1.5 rounded-full bg-slate-700/50 px-3.5 py-2 text-sm font-medium text-slate-400 hover:bg-slate-700 hover:text-slate-300 disabled:opacity-50 transition-all"
                  data-testid="video-upload-btn"
                >
                  {uploadingVideo ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Video className="h-4 w-4" />
                  )}
                  {uploadingVideo ? t("loading") : videoUrl ? "1/1" : t("video")}
                </button>
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    handleVideoUpload(file);
                  }}
                  data-testid="video-file-input"
                />
                
                {questionText.length > 0 && (
                <span className="ml-auto text-sm text-slate-500">
                  {questionText.length}/2000
                </span>
                )}
              </div>
              )}
              
              {/* 18+ Age Consent Checkbox - Always visible when form has content */}
              {(formFocused || questionText.length > 0) && (
              <div className="flex items-start gap-3 px-3 pt-3 mt-2">
                <input
                  type="checkbox"
                  id="age-consent"
                  checked={is18Plus}
                  onChange={(e) => setIs18Plus(e.target.checked)}
                  className="w-5 h-5 mt-0.5 rounded border-slate-600 bg-slate-800 text-indigo-500 focus:ring-indigo-500 focus:ring-offset-slate-900 flex-shrink-0"
                  data-testid="age-consent-checkbox"
                />
                <label htmlFor="age-consent" className="text-sm text-slate-400 cursor-pointer select-none leading-relaxed">
                  I confirm that I am <span className="text-indigo-400 font-medium">18 years or older</span>
                </label>
              </div>
              )}
            </div>
            <p className="mt-3 text-xs text-slate-600">
              No login required to ask • Your identity stays hidden
            </p>
            
            {/* Secondary CTA */}
            <div className="mt-4">
              <Button
                variant="outline"
                onClick={() => navigate("/browse")}
                className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white hover:border-indigo-500"
                data-testid="browse-questions-btn"
              >
                Browse questions
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="border-y border-slate-800 bg-slate-900/50 backdrop-blur-sm">
        <div className="mx-auto max-w-6xl px-4 py-4">
          <div className="flex flex-wrap items-center justify-center gap-8 text-center">
            <div>
              <div className="text-2xl font-bold text-white">{stats.questions}</div>
              <div className="text-xs text-slate-500 uppercase tracking-wide">Questions</div>
            </div>
            <div className="h-8 w-px bg-slate-700" />
            <div>
              <div className="text-2xl font-bold text-white">{stats.answers}</div>
              <div className="text-xs text-slate-500 uppercase tracking-wide">Answers</div>
            </div>
            <div className="h-8 w-px bg-slate-700" />
            <div>
              <div className="text-2xl font-bold text-emerald-400">100%</div>
              <div className="text-xs text-slate-500 uppercase tracking-wide">Anonymous</div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories - Clear Filter Section */}
      <section className="px-4 py-10">
        <div className="mx-auto max-w-6xl">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-200 flex items-center gap-2">
                <span className="w-1 h-5 bg-indigo-500 rounded-full"></span>
                Browse by topic
              </h2>
              <p className="text-sm text-slate-500 mt-1">Click a category to filter questions</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <CategoryCard 
              icon={Users} 
              title={t("students")} 
              count={categoryCounts["Students"] || 0}
              onClick={() => navigate("/browse?category=Students")}
            />
            <CategoryCard 
              icon={Users} 
              title={t("tenants")} 
              count={categoryCounts["Tenants"] || 0}
              onClick={() => navigate("/browse?category=Tenants")}
            />
            <CategoryCard 
              icon={Sparkles} 
              title={t("itWork")} 
              count={categoryCounts["IT Work"] || 0}
              onClick={() => navigate("/browse?category=IT Work")}
            />
            <CategoryCard 
              icon={TrendingUp} 
              title={t("local")} 
              count={categoryCounts["Local"] || 0}
              onClick={() => navigate("/browse?category=Local")}
            />
          </div>
        </div>
      </section>

      {/* Questions Feed */}
      <section className="px-4 py-10">
        <div className="mx-auto max-w-6xl">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-200 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-indigo-400" />
              {t("recentQuestions")}
            </h2>
            <Button
              variant="ghost"
              onClick={() => navigate("/browse")}
              className="text-slate-400 hover:text-white hover:bg-slate-800"
            >
              {t("viewMore")}
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
            </div>
          ) : questions.length === 0 ? (
            <Card className="border-slate-700 bg-slate-800/50 p-12 text-center">
              <MessageCircle className="mx-auto h-12 w-12 text-slate-600 mb-4" />
              <h3 className="text-lg font-medium text-slate-300 mb-2">{t("noQuestions")}</h3>
              <p className="text-slate-500 mb-4">{t("beFirst")}</p>
              <Button 
                onClick={() => inputRef.current?.focus()}
                className="btn-indigo btn-press"
              >
                {t("askButton")}
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {questions.map((q, i) => (
                <div key={q.slug} className="animate-fadeInUp" style={{ animationDelay: `${i * 0.05}s` }}>
                  <QuestionCard 
                    question={q} 
                    onClick={() => navigate(`/q/${q.slug}`)}
                    language={language}
                    getTranslatedContent={getTranslatedContent}
                    isAdmin={isAdmin}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Load More */}
          {questions.length > 0 && (
            <div className="mt-10 text-center">
              <Button
                variant="outline"
                onClick={() => navigate("/browse")}
                className="border-slate-700 bg-slate-800/50 text-slate-300 hover:bg-slate-800 hover:text-white hover:border-indigo-500/50 px-8"
              >
                {t("browse")}
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 bg-slate-950 px-4 py-8">
        <div className="mx-auto max-w-6xl text-center">
          <div className="text-slate-400 font-medium mb-2">askincog.com</div>
          <p className="text-sm text-slate-600">
            Bangalore&apos;s anonymous community journal • Built with trust
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Ask anonymously. Answer responsibly.
          </p>
          
          {/* Social Links - Only verified active profiles */}
          <div className="mt-4 flex items-center justify-center gap-4">
            {/* Instagram - Verified */}
            <a 
              href="https://instagram.com/askincog24" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-800 hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-500 transition-all"
              aria-label="Instagram"
            >
              <svg className="w-5 h-5 text-slate-400 hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            {/* X (Twitter) - Verified */}
            <a 
              href="https://x.com/askincog247" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-800 hover:bg-black transition-colors"
              aria-label="X (Twitter)"
            >
              <svg className="w-5 h-5 text-slate-400 hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
            {/* YouTube - Verified channel URL */}
            <a 
              href="https://www.youtube.com/channel/UCoL-ILD6ESPXHbsurJqxmng" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-800 hover:bg-red-600 transition-colors"
              aria-label="YouTube"
            >
              <svg className="w-5 h-5 text-slate-400 hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </a>
            {/* Facebook - Hidden until exact page URL is provided */}
            {/* TODO: Add Facebook icon when exact page URL is confirmed */}
            {/* LinkedIn - NOT available, intentionally hidden */}
          </div>
          
          <div className="mt-4 flex items-center justify-center gap-6 text-xs text-slate-600">
            <a href="/about" className="hover:text-slate-400 transition-colors">About</a>
            <a href="/terms" className="hover:text-slate-400 transition-colors">Terms</a>
            <a href="/privacy" className="hover:text-slate-400 transition-colors">Privacy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
